// Wave 2 collaboration header for the Artifacts tab: fork lineage (one
// hop each way), outgoing references with source-drift badges and a
// one-click re-reference, and attribution deep-links into the source
// deck's slides.
import { useEffect, useState } from 'react';
import type { ViewerTarget } from './ArtifactViewer';

interface OutgoingRef {
  source_deck_id: string;
  source_title: string | null;
  source_version: string;
  source_slide_num: number;
  kind: 'fork' | 'quote';
  source_changed: boolean;
}
interface RefsPayload {
  forked_from: { id: string; title: string } | null;
  forks: Array<{ id: string; title: string }>;
  outgoing: OutgoingRef[];
}

export default function DeckCollabPanel({
  deckId,
  onOpenSource,
}: {
  deckId: string;
  onOpenSource: (t: ViewerTarget) => void;
}) {
  const [data, setData] = useState<RefsPayload | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = () => {
    fetch(`/api/decks/${deckId}/references`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => setData(d))
      .catch(() => {});
  };
  useEffect(load, [deckId]);

  if (!data) return null;
  const hasAnything = data.forked_from || data.forks.length > 0 || data.outgoing.length > 0;
  if (!hasAnything) return null;

  const reReference = async (r: OutgoingRef) => {
    const key = `${r.source_deck_id}:${r.source_slide_num}`;
    setBusy(key);
    try {
      await fetch(`/api/decks/${deckId}/re-reference`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deck_id: r.source_deck_id, version: r.source_version, slide_num: r.source_slide_num }),
      });
      load();
    } finally {
      setBusy(null);
    }
  };

  const deckLink = (id: string, title: string) => (
    <a href={`/deck/${id}`} style={{ color: 'var(--ds-accent)', textDecoration: 'none' }}>{title}</a>
  );

  return (
    <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--ds-surface-subtle)', fontSize: 12 }}>
      {data.forked_from && (
        <div style={{ marginBottom: 6 }}>⑂ Forked from {deckLink(data.forked_from.id, data.forked_from.title)}</div>
      )}
      {data.forks.length > 0 && (
        <div style={{ marginBottom: 6 }}>
          ⑂ {data.forks.length} fork{data.forks.length > 1 ? 's' : ''}:{' '}
          {data.forks.slice(0, 5).map((f, i) => (
            <span key={f.id}>{i > 0 && ' · '}{deckLink(f.id, f.title)}</span>
          ))}
        </div>
      )}
      {data.outgoing.length > 0 && (
        <div>
          <div style={{ opacity: 0.6, marginBottom: 4 }}>References {data.outgoing.length} slide{data.outgoing.length > 1 ? 's' : ''}:</div>
          {data.outgoing.slice(0, 12).map(r => {
            const key = `${r.source_deck_id}:${r.source_slide_num}`;
            return (
              <div key={`${key}:${r.kind}`} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0' }}>
                <span
                  onClick={() =>
                    onOpenSource({
                      deckId: r.source_deck_id,
                      version: r.source_version,
                      filename: `slide-${String(r.source_slide_num).padStart(3, '0')}.html`,
                      mime: 'text/html',
                      source: { deckTitle: r.source_title ?? r.source_deck_id, slideNum: r.source_slide_num, deckId: r.source_deck_id, version: r.source_version },
                    })
                  }
                  style={{ cursor: 'pointer', color: 'var(--ds-accent)' }}
                  title="Open source slide"
                >
                  {r.kind === 'fork' ? '⑂' : '↩'} {r.source_title ?? r.source_deck_id} · {r.source_version} · slide {r.source_slide_num}
                </span>
                {r.source_changed && (
                  <>
                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 8, background: 'var(--ds-warning-soft)', color: 'var(--ds-warning)' }}>source updated</span>
                    <button
                      onClick={() => reReference(r)}
                      disabled={busy === key}
                      style={{ fontSize: 10, padding: '1px 8px', borderRadius: 8, border: '1px solid var(--ds-overlay)', background: 'none', cursor: 'pointer' }}
                    >{busy === key ? '…' : 'Re-reference'}</button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
